import {
  AGGREGATOR_CARGO_TYPES,
  CARGO_TYPES,
  PROPERTY_GROUP_KEYS,
  RATING_UOMS,
  addGroup,
  getCargoTypeLabel,
  getRatingUomLabel,
  getVariantPresets,
  groupHasValues,
  isAggregatorCargoType,
  isGroupActive,
  removeGroup,
  type CargoPropertyGroupsPayload,
  type CargoTypeValue,
  type CargoVariantPreset,
  type PropertyGroupKey,
  type RatingUomValue,
  type ServiceTag,
} from '@my/tms-api';
import type { OrganisationAccountingCategory, OrganisationTaxRate } from '@my/directory-api';
import { SelectInput, Switch, TextInput } from '@my/ui';
import { Check, ChevronRight, Package, Plus, Trash2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CargoProductsPanel } from './CargoProductsPanel';
import { categoryOptions } from './lib/accountingCategoryOptions';
import { taxRateLabel, taxRateOptions } from './lib/taxRateOptions';
import {
  emptyCustomVariant,
  type CargoDefinitionDraft,
  type CustomVariantDraft,
} from './lib/cargoDefinitionDraft';
import {
  ALLOWED_LINE_TYPES,
  PROPERTY_GROUP_HELPER,
  PROPERTY_GROUP_LABELS,
  describeFilledGroup,
  describePromptedGroup,
} from './lib/cargoTypeConfig';

export interface CargoDefinitionEditorProps {
  draft: CargoDefinitionDraft;
  /** True for an unsaved row (cargo-type select stays unlocked). */
  isNew: boolean;
  onChange: (next: CargoDefinitionDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  /** Hidden when undefined (e.g. on the +Add row). */
  onDelete?: () => void;
  isBusy: boolean;
  /** Catalogue keys the org has selected on Equipment Definitions. */
  availableEquipment: Array<{ key: string; label: string }>;
  /** Active service tags applying to Cargo (or the all-areas sentinel). */
  availableServiceTags: ServiceTag[];
  /** Owning organisation — the Products panel lists its products. */
  organisationId: string | null;
  /**
   * The organisation has products switched on. Off, the Products chip is
   * hidden — unless this definition already has products, which stay
   * visible (and are saved back untouched) rather than silently dropped.
   */
  productsEnabled: boolean;
  /**
   * The organisation's accounting categories, or null when it does not use
   * them — then neither the cargo nor the product pickers show.
   */
  accountingCategories: OrganisationAccountingCategory[] | null;
  /** The organisation's tax schemas — the VAT-rate overrides choose from these. */
  taxRates: OrganisationTaxRate[];
}

/**
 * Inline editor for a single `CargoDefinition` row — new (variants +
 * property groups) shape.
 *
 * <p>
 * Layout (top → bottom):
 * <ul>
 *   <li>Cargo Type select (locked on edit) + Display Name (auto-fills
 *       from the type label until the operator customises it).</li>
 *   <li>Variants — preset chip multi-select + custom variant inline
 *       editor (Pallet asks LM, Iso Tank / Container ask PerTrailer,
 *       Livestock asks max transit hours, Pharma asks temp band,
 *       everything else asks dimensions). Hidden entirely for aggregator
 *       types (FullLoad / PartLoad).</li>
 *   <li>Unit of Measure chips — all 9, universal.</li>
 *   <li>Equipment chips — sourced from the org's Equipment Definitions.</li>
 *   <li>Properties chip row — all 7 groups, universal. Each ticked group
 *       expands inline with its spec fields; leaving them blank means
 *       "ask at order entry".</li>
 *   <li>Service Tag chips — filtered to ones applying to Cargo.</li>
 *   <li>Action row — Delete / Cancel / Save.</li>
 * </ul>
 * </p>
 */
export function CargoDefinitionEditor({
  draft,
  isNew,
  onChange,
  onSave,
  onCancel,
  onDelete,
  isBusy,
  availableEquipment,
  availableServiceTags,
  organisationId,
  productsEnabled,
  accountingCategories,
  taxRates,
}: CargoDefinitionEditorProps) {
  const { t } = useTranslation('configuration');

  const isAggregator = isAggregatorCargoType(draft.cargoType);
  const variantPresets = getVariantPresets(draft.cargoType);

  const setField = <K extends keyof CargoDefinitionDraft>(key: K, value: CargoDefinitionDraft[K]) =>
    onChange({ ...draft, [key]: value });

  const handleCargoTypeChange = (raw: string) => {
    const next = raw as CargoTypeValue;
    const prevLabel = getCargoTypeLabel(draft.cargoType);
    const nextLabel = getCargoTypeLabel(next);
    onChange({
      ...draft,
      cargoType: next,
      // If the operator hasn't customised Display Name yet, keep it in
      // sync with the new type's label so a +Add row stays savable.
      displayName: draft.displayName === prevLabel ? nextLabel : draft.displayName,
      // Variants belong to a specific type — drop everything when the
      // type changes. UoMs + property groups are universal so they stay.
      variantPresetIds: [],
      customVariants: [],
    });
  };

  const toggleUom = (uom: RatingUomValue) => {
    setField(
      'unitsOfMeasure',
      draft.unitsOfMeasure.includes(uom)
        ? draft.unitsOfMeasure.filter((u) => u !== uom)
        : [...draft.unitsOfMeasure, uom],
    );
  };

  const toggleEquipment = (key: string) => {
    setField(
      'equipmentKeys',
      draft.equipmentKeys.includes(key)
        ? draft.equipmentKeys.filter((k) => k !== key)
        : [...draft.equipmentKeys, key],
    );
  };

  const toggleTag = (id: string) => {
    setField(
      'serviceTagIds',
      draft.serviceTagIds.includes(id)
        ? draft.serviceTagIds.filter((tagId) => tagId !== id)
        : [...draft.serviceTagIds, id],
    );
  };

  const togglePresetVariant = (id: string) => {
    setField(
      'variantPresetIds',
      draft.variantPresetIds.includes(id)
        ? draft.variantPresetIds.filter((v) => v !== id)
        : [...draft.variantPresetIds, id],
    );
  };

  const addCustomVariant = () =>
    setField('customVariants', [...draft.customVariants, emptyCustomVariant()]);

  const patchCustomVariant = (localId: string, patch: Partial<CustomVariantDraft>) =>
    setField(
      'customVariants',
      draft.customVariants.map((c) => (c.localId === localId ? { ...c, ...patch } : c)),
    );

  const removeCustomVariant = (localId: string) =>
    setField(
      'customVariants',
      draft.customVariants.filter((c) => c.localId !== localId),
    );

  const toggleGroup = (key: PropertyGroupKey) => {
    setField(
      'propertyGroups',
      isGroupActive(key, draft.propertyGroups)
        ? removeGroup(key, draft.propertyGroups)
        : addGroup(key, draft.propertyGroups),
    );
  };

  const patchGroup = <K extends keyof CargoPropertyGroupsPayload>(
    key: K,
    next: CargoPropertyGroupsPayload[K],
  ) => setField('propertyGroups', { ...draft.propertyGroups, [key]: next });

  // Save-disabled conditions:
  //  - missing display name
  //  - non-aggregator type with no variants ticked (BE validator would reject)
  const hasVariants = draft.variantPresetIds.length > 0 || draft.customVariants.some((c) => c.name.trim().length > 0);
  const hasError =
    !draft.displayName.trim()
    || draft.unitsOfMeasure.length === 0
    || (!isAggregator && !hasVariants);

  return (
    <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
      {/* 2-col layout: form takes the left 2/3, the live order-line
          preview sits on the right 1/3. Collapses to single-column on
          narrow viewports so the preview shows below the form rather
          than getting squashed into a 1/3 slice of a phone screen.
          The action row below the grid stays full-width either way. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* ── Left/middle: form ── */}
        <div className="space-y-3 lg:col-span-2">
      {/* Cargo Type + Display Name */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <SelectInput
          label={requiredLabel(t('cargoDefinitions.fields.cargoType'))}
          name="cargoType"
          value={draft.cargoType}
          onChange={(e) => handleCargoTypeChange(e.target.value)}
          disabled={!isNew || isBusy}
          helperText={
            !isNew
              ? t('cargoDefinitions.fields.cargoTypeLocked')
              : undefined
          }
          options={CARGO_TYPES.map((ct) => ({ value: ct, label: getCargoTypeLabel(ct) }))}
        />
        <TextInput
          label={requiredLabel(t('cargoDefinitions.fields.displayName'))}
          name="displayName"
          value={draft.displayName}
          maxLength={128}
          onChange={(e) => setField('displayName', e.target.value)}
          placeholder={getCargoTypeLabel(draft.cargoType)}
          disabled={isBusy}
          error={!draft.displayName.trim()}
          errorMessage={t('cargoDefinitions.fields.displayNameRequired')}
        />
      </div>

      {/* Accounting (category only while the organisation uses them) — its
          own line, as on Charges & Costs, so the identity row stays readable. */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {accountingCategories && (
          <SelectInput
            label={t('cargoDefinitions.accountingCategory.label')}
            name="accountingCategoryCode"
            value={draft.accountingCategoryCode}
            onChange={(e) => setField('accountingCategoryCode', e.target.value)}
            disabled={isBusy}
            helperText={
              draft.hasProducts ? t('cargoDefinitions.accountingCategory.productsInherit') : undefined
            }
            options={categoryOptions(
              accountingCategories,
              draft.accountingCategoryCode,
              t('cargoDefinitions.accountingCategory.none'),
              (code) => t('cargoDefinitions.accountingCategory.notInList', { code }),
            )}
          />
        )}
        <SelectInput
          label={t('cargoDefinitions.vat.label')}
          name="taxRateId"
          value={draft.taxRateId}
          onChange={(e) => setField('taxRateId', e.target.value)}
          disabled={isBusy}
          title={t('cargoDefinitions.vat.hint')}
          helperText={draft.hasProducts ? t('cargoDefinitions.vat.productsInherit') : undefined}
          options={taxRateOptions(
            taxRates,
            draft.taxRateId,
            t('cargoDefinitions.vat.none'),
            t('cargoDefinitions.vat.notInList'),
          )}
        />
      </div>

      {/* Variants — non-aggregator only */}
      {isAggregator ? (
        <div className="rounded-md border border-blue-200 bg-blue-50/40 p-2 text-[11px] text-blue-900">
          {t('cargoDefinitions.fields.aggregatorHelper', {
            type: getCargoTypeLabel(draft.cargoType),
          })}
        </div>
      ) : (
        <VariantsSection
          type={draft.cargoType}
          presets={variantPresets}
          selectedIds={draft.variantPresetIds}
          customVariants={draft.customVariants}
          onTogglePreset={togglePresetVariant}
          onAddCustom={addCustomVariant}
          onPatchCustom={patchCustomVariant}
          onRemoveCustom={removeCustomVariant}
          isBusy={isBusy}
        />
      )}

      {/* Unit of Measure */}
      <FieldGroup label={requiredLabel(t('cargoDefinitions.fields.unitOfMeasure'))}>
        <div className="flex flex-wrap gap-1.5">
          {RATING_UOMS.map((u) => (
            <ChipButton
              key={u}
              active={draft.unitsOfMeasure.includes(u)}
              onClick={() => toggleUom(u)}
              disabled={isBusy}
              tone="violet"
            >
              {getRatingUomLabel(u)}
            </ChipButton>
          ))}
        </div>
      </FieldGroup>

      {/* Equipment */}
      <FieldGroup label={t('cargoDefinitions.fields.equipment')}>
        {availableEquipment.length === 0 ? (
          <p className="text-xs italic text-slate-400">{t('cargoDefinitions.fields.noEquipment')}</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {availableEquipment.map((eq) => (
              <ChipButton
                key={eq.key}
                active={draft.equipmentKeys.includes(eq.key)}
                onClick={() => toggleEquipment(eq.key)}
                disabled={isBusy}
                tone="sky"
                title={eq.key}
              >
                {eq.label}
              </ChipButton>
            ))}
          </div>
        )}
      </FieldGroup>

      {/* Property groups */}
      <FieldGroup label={t('cargoDefinitions.fields.propertiesSpecOrPrompt')}>
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {PROPERTY_GROUP_KEYS.map((key) => (
              <ChipButton
                key={key}
                active={isGroupActive(key, draft.propertyGroups)}
                onClick={() => toggleGroup(key)}
                disabled={isBusy}
                tone="slate"
              >
                {PROPERTY_GROUP_LABELS[key]}
              </ChipButton>
            ))}
            {/* Products: not a property group (it is not stored in the groups
                document) but it reads as one — "this cargo carries products". */}
            {(productsEnabled || draft.hasProducts) && (
              <ChipButton
                active={draft.hasProducts}
                onClick={() =>
                  onChange({
                    ...draft,
                    hasProducts: !draft.hasProducts,
                    // Off drops the links, as the server does.
                    products: draft.hasProducts ? [] : draft.products,
                  })
                }
                disabled={isBusy}
                tone="slate"
              >
                {t('cargoDefinitions.products.chip')}
              </ChipButton>
            )}
          </div>
          {PROPERTY_GROUP_KEYS.filter((g) => isGroupActive(g, draft.propertyGroups)).map((g) => (
            <PropertyGroupFields
              key={g}
              group={g}
              payload={draft.propertyGroups}
              onChange={patchGroup}
              disabled={isBusy}
            />
          ))}
          {draft.hasProducts && (
            <CargoProductsPanel
              organisationId={organisationId}
              accountingCategories={accountingCategories}
              inheritedCategoryCode={draft.accountingCategoryCode}
              taxRates={taxRates}
              inheritedTaxRateLabel={(() => {
                const r = taxRates.find((x) => x.id === draft.taxRateId);
                return r ? taxRateLabel(r) : null;
              })()}
              products={draft.products}
              onChange={(products) => setField('products', products)}
              disabled={isBusy}
            />
          )}
        </div>
      </FieldGroup>

      {/* Service Tags */}
      <FieldGroup label={t('cargoDefinitions.fields.serviceTags')}>
        {availableServiceTags.length === 0 ? (
          <p className="text-xs italic text-slate-400">
            {t('cargoDefinitions.fields.noServiceTags')}
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {availableServiceTags.map((tag) => (
              <ChipButton
                key={tag.id}
                active={draft.serviceTagIds.includes(tag.id)}
                onClick={() => toggleTag(tag.id)}
                disabled={isBusy}
                tone="amber"
              >
                {tag.name}
              </ChipButton>
            ))}
          </div>
        )}
      </FieldGroup>
        </div>

        {/* ── Right: live order-line preview ── */}
        <aside className="rounded-md border border-slate-200 bg-white p-3 lg:max-h-[80vh] lg:overflow-auto">
          <OrderLinePreview
            draft={draft}
            variantPresets={variantPresets}
            availableServiceTags={availableServiceTags}
          />
        </aside>
      </div>

      {/* Action row */}
      <div className="flex items-center justify-between gap-2 pt-1">
        <div>
          {!isNew && onDelete && (
            <button
              type="button"
              aria-label={t('cargoDefinitions.ariaDelete')}
              onClick={onDelete}
              disabled={isBusy}
              className="flex h-7 w-7 items-center justify-center rounded-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label={t('cargoDefinitions.ariaCancel')}
            onClick={onCancel}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200 disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label={t('cargoDefinitions.ariaSave')}
            onClick={onSave}
            disabled={isBusy || hasError}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Variants section — preset multi-select + custom variant inline list
// ────────────────────────────────────────────────────────────────────────────

interface VariantsSectionProps {
  type: CargoTypeValue;
  presets: CargoVariantPreset[];
  selectedIds: string[];
  customVariants: CustomVariantDraft[];
  onTogglePreset: (id: string) => void;
  onAddCustom: () => void;
  onPatchCustom: (localId: string, patch: Partial<CustomVariantDraft>) => void;
  onRemoveCustom: (localId: string) => void;
  isBusy: boolean;
}

function VariantsSection({
  type,
  presets,
  selectedIds,
  customVariants,
  onTogglePreset,
  onAddCustom,
  onPatchCustom,
  onRemoveCustom,
  isBusy,
}: VariantsSectionProps) {
  const { t } = useTranslation('configuration');
  return (
    <FieldGroup
      label={t('cargoDefinitions.fields.variants', { type: getCargoTypeLabel(type) })}
    >
      <p className="pb-1 text-[11px] italic text-slate-500">
        {t('cargoDefinitions.fields.variantsHelper')}
      </p>
      {presets.length === 0 ? (
        <p className="text-[11px] italic text-slate-400">
          {t('cargoDefinitions.fields.noPresets')}
        </p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {presets.map((v) => (
            <ChipButton
              key={v.id}
              active={selectedIds.includes(v.id)}
              onClick={() => onTogglePreset(v.id)}
              disabled={isBusy}
              tone="purple"
            >
              {v.name}
            </ChipButton>
          ))}
        </div>
      )}

      <div className="space-y-2 pt-2">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-medium text-slate-600">
            {t('cargoDefinitions.fields.customVariants')}
          </p>
          <button
            type="button"
            onClick={onAddCustom}
            disabled={isBusy}
            className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[11px] font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
          >
            <Plus className="h-3 w-3" />
            {t('cargoDefinitions.fields.addCustomVariant')}
          </button>
        </div>
        {customVariants.length === 0 ? (
          <p className="text-[11px] italic text-slate-400">
            {t('cargoDefinitions.fields.noCustomVariants')}
          </p>
        ) : (
          <div className="space-y-2">
            {customVariants.map((c) => (
              <CustomVariantRow
                key={c.localId}
                type={type}
                variant={c}
                onPatch={(patch) => onPatchCustom(c.localId, patch)}
                onRemove={() => onRemoveCustom(c.localId)}
                disabled={isBusy}
              />
            ))}
          </div>
        )}
      </div>
    </FieldGroup>
  );
}

/**
 * Per-type pivot for the custom-variant row form:
 *   - Pallet → Name + LM
 *   - Iso Tank / Container → Name + Per Trailer (1 or 2)
 *   - Livestock → Name + Max Transit (hours)
 *   - Pharmaceutical → Name + Min/Max °C
 *   - Agri Grains / Bulk Liquids / Bulk Materials → Name only
 *   - Everything else → Name + L/W/H/Tare
 */
function CustomVariantRow({
  type,
  variant: c,
  onPatch,
  onRemove,
  disabled,
}: {
  type: CargoTypeValue;
  variant: CustomVariantDraft;
  onPatch: (patch: Partial<CustomVariantDraft>) => void;
  onRemove: () => void;
  disabled: boolean;
}) {
  const { t } = useTranslation('configuration');

  if (type === 'Pallet') {
    return (
      <CustomVariantShell onRemove={onRemove} disabled={disabled} cols="md:grid-cols-[2fr_1fr_auto]">
        <TextInput
          label={t('cargoDefinitions.fields.variantName')}
          name={`cv-name-${c.localId}`}
          value={c.name}
          onChange={(e) => onPatch({ name: e.target.value })}
          disabled={disabled}
        />
        <NumberInput
          label={t('cargoDefinitions.fields.loadingMetres')}
          value={c.loadingMetres}
          onChange={(n) => onPatch({ loadingMetres: n })}
          disabled={disabled}
        />
        <RemoveButton onRemove={onRemove} disabled={disabled} />
      </CustomVariantShell>
    );
  }

  if (type === 'IsoTank' || type === 'Container') {
    return (
      <CustomVariantShell onRemove={onRemove} disabled={disabled} cols="md:grid-cols-[2fr_1fr_auto]">
        <TextInput
          label={t('cargoDefinitions.fields.variantName')}
          name={`cv-name-${c.localId}`}
          value={c.name}
          onChange={(e) => onPatch({ name: e.target.value })}
          disabled={disabled}
        />
        <SelectInput
          label={t('cargoDefinitions.fields.perTrailer')}
          name={`cv-pt-${c.localId}`}
          value={c.perTrailer == null ? '' : String(c.perTrailer)}
          onChange={(e) =>
            onPatch({
              perTrailer: e.target.value === '' ? null : Number(e.target.value),
            })
          }
          disabled={disabled}
          options={[
            { value: '', label: '—' },
            { value: '1', label: t('cargoDefinitions.fields.perTrailer1') },
            { value: '2', label: t('cargoDefinitions.fields.perTrailer2') },
          ]}
        />
        <RemoveButton onRemove={onRemove} disabled={disabled} />
      </CustomVariantShell>
    );
  }

  if (type === 'Livestock') {
    return (
      <CustomVariantShell onRemove={onRemove} disabled={disabled} cols="md:grid-cols-[2fr_1fr_auto]">
        <TextInput
          label={t('cargoDefinitions.fields.variantSpecies')}
          name={`cv-name-${c.localId}`}
          value={c.name}
          onChange={(e) => onPatch({ name: e.target.value })}
          disabled={disabled}
        />
        <NumberInput
          label={t('cargoDefinitions.fields.maxTransitHours')}
          value={c.maxTransitHours}
          onChange={(n) => onPatch({ maxTransitHours: n })}
          disabled={disabled}
        />
        <RemoveButton onRemove={onRemove} disabled={disabled} />
      </CustomVariantShell>
    );
  }

  if (type === 'Pharmaceutical') {
    return (
      <CustomVariantShell onRemove={onRemove} disabled={disabled} cols="md:grid-cols-[2fr_1fr_1fr_auto]">
        <TextInput
          label={t('cargoDefinitions.fields.variantName')}
          name={`cv-name-${c.localId}`}
          value={c.name}
          onChange={(e) => onPatch({ name: e.target.value })}
          disabled={disabled}
        />
        <NumberInput
          label={t('cargoDefinitions.fields.minTempC')}
          value={c.minTempC}
          onChange={(n) => onPatch({ minTempC: n })}
          allowNegative
          disabled={disabled}
        />
        <NumberInput
          label={t('cargoDefinitions.fields.maxTempC')}
          value={c.maxTempC}
          onChange={(n) => onPatch({ maxTempC: n })}
          allowNegative
          disabled={disabled}
        />
        <RemoveButton onRemove={onRemove} disabled={disabled} />
      </CustomVariantShell>
    );
  }

  if (type === 'AgriGrains' || type === 'BulkLiquids' || type === 'BulkMaterials') {
    return (
      <CustomVariantShell onRemove={onRemove} disabled={disabled} cols="md:grid-cols-[1fr_auto]">
        <TextInput
          label={t('cargoDefinitions.fields.variantName')}
          name={`cv-name-${c.localId}`}
          value={c.name}
          onChange={(e) => onPatch({ name: e.target.value })}
          disabled={disabled}
        />
        <RemoveButton onRemove={onRemove} disabled={disabled} />
      </CustomVariantShell>
    );
  }

  // Everything else (Construction, custom Iso Tanks with full dims, etc.)
  return (
    <CustomVariantShell onRemove={onRemove} disabled={disabled} cols="md:grid-cols-6">
      <div className="md:col-span-2">
        <TextInput
          label={t('cargoDefinitions.fields.variantName')}
          name={`cv-name-${c.localId}`}
          value={c.name}
          onChange={(e) => onPatch({ name: e.target.value })}
          disabled={disabled}
        />
      </div>
      <NumberInput
        label={t('cargoDefinitions.fields.lengthCm')}
        value={c.lengthCm}
        onChange={(n) => onPatch({ lengthCm: n })}
        disabled={disabled}
      />
      <NumberInput
        label={t('cargoDefinitions.fields.widthCm')}
        value={c.widthCm}
        onChange={(n) => onPatch({ widthCm: n })}
        disabled={disabled}
      />
      <NumberInput
        label={t('cargoDefinitions.fields.heightCm')}
        value={c.heightCm}
        onChange={(n) => onPatch({ heightCm: n })}
        disabled={disabled}
      />
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <NumberInput
            label={t('cargoDefinitions.fields.tareWeightKg')}
            value={c.tareWeightKg}
            onChange={(n) => onPatch({ tareWeightKg: n })}
            disabled={disabled}
          />
        </div>
        <RemoveButton onRemove={onRemove} disabled={disabled} />
      </div>
    </CustomVariantShell>
  );
}

function CustomVariantShell({
  children,
  cols,
  onRemove: _onRemove,
  disabled: _disabled,
}: {
  children: React.ReactNode;
  cols: string;
  onRemove: () => void;
  disabled: boolean;
}) {
  // The remove button is supplied by the per-type pivot rows above (so the
  // grid column count matches the row shape). This shell just provides the
  // shared border + padding + grid frame.
  return (
    <div className={`grid grid-cols-1 gap-2 rounded-md border border-purple-200 bg-purple-50/40 p-2 ${cols}`}>
      {children}
    </div>
  );
}

function RemoveButton({ onRemove, disabled }: { onRemove: () => void; disabled: boolean }) {
  const { t } = useTranslation('configuration');
  return (
    <div className="flex items-end">
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        aria-label={t('cargoDefinitions.fields.removeCustomVariant')}
        className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Property group expanded forms — each ticked group renders its sub-fields
// ────────────────────────────────────────────────────────────────────────────

interface PropertyGroupFieldsProps {
  group: PropertyGroupKey;
  payload: CargoPropertyGroupsPayload;
  onChange: <K extends keyof CargoPropertyGroupsPayload>(
    key: K,
    next: CargoPropertyGroupsPayload[K],
  ) => void;
  disabled?: boolean;
}

function PropertyGroupFields({ group, payload, onChange, disabled }: PropertyGroupFieldsProps) {
  const { t } = useTranslation('configuration');
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <p className="pb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        {PROPERTY_GROUP_LABELS[group]}
      </p>
      {group === 'capacity' && (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
          <NumberInput
            label={t('cargoDefinitions.fields.maxWeightKg')}
            value={payload.capacity?.maxWeightKg ?? null}
            onChange={(n) => onChange('capacity', { ...payload.capacity, maxWeightKg: n })}
            disabled={disabled}
          />
          <NumberInput
            label={t('cargoDefinitions.fields.maxVolumeM3')}
            value={payload.capacity?.maxVolumeM3 ?? null}
            onChange={(n) => onChange('capacity', { ...payload.capacity, maxVolumeM3: n })}
            disabled={disabled}
          />
          <NumberInput
            label={t('cargoDefinitions.fields.maxPallets')}
            value={payload.capacity?.maxPallets ?? null}
            onChange={(n) => onChange('capacity', { ...payload.capacity, maxPallets: n })}
            disabled={disabled}
          />
        </div>
      )}
      {group === 'handling' && (
        <TextInput
          label={t('cargoDefinitions.fields.handlingNotes')}
          name="handlingNotes"
          value={payload.handling?.notes ?? ''}
          maxLength={1024}
          onChange={(e) => onChange('handling', { notes: e.target.value })}
          placeholder={t('cargoDefinitions.fields.handlingPlaceholder')}
          disabled={disabled}
        />
      )}
      {group === 'temperature' && (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
          <NumberInput
            label={t('cargoDefinitions.fields.minTempC')}
            value={payload.temperature?.minTempC ?? null}
            onChange={(n) => onChange('temperature', { ...payload.temperature, minTempC: n })}
            allowNegative
            disabled={disabled}
          />
          <NumberInput
            label={t('cargoDefinitions.fields.maxTempC')}
            value={payload.temperature?.maxTempC ?? null}
            onChange={(n) => onChange('temperature', { ...payload.temperature, maxTempC: n })}
            allowNegative
            disabled={disabled}
          />
          <NumberInput
            label={t('cargoDefinitions.fields.maxTransitDays')}
            value={payload.temperature?.maxTransitDays ?? null}
            onChange={(n) => onChange('temperature', { ...payload.temperature, maxTransitDays: n })}
            disabled={disabled}
          />
        </div>
      )}
      {group === 'stack' && (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <NumberInput
            label={t('cargoDefinitions.fields.maxStackHeightCm')}
            value={payload.stack?.maxStackHeightCm ?? null}
            onChange={(n) => onChange('stack', { ...payload.stack, maxStackHeightCm: n })}
            disabled={disabled}
          />
          <div className="flex items-end">
            <Switch
              label={t('cargoDefinitions.fields.doubleStackPermitted')}
              name="doubleStackPermitted"
              checked={payload.stack?.doubleStackPermitted ?? false}
              onChange={(e) =>
                onChange('stack', { ...payload.stack, doubleStackPermitted: e.currentTarget.checked })
              }
              disabled={disabled}
            />
          </div>
        </div>
      )}
      {group === 'bic' && (
        <p className="text-[11px] italic text-slate-500">
          {t('cargoDefinitions.fields.bicHelper')}
        </p>
      )}
      {group === 'compartments' && (
        <p className="text-[11px] italic text-slate-500">
          {t('cargoDefinitions.fields.compartmentsHelper')}
        </p>
      )}
      {group === 'compliance' && (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
          <TextInput
            label={t('cargoDefinitions.fields.complianceStandard')}
            name="complianceStandard"
            value={payload.compliance?.standard ?? ''}
            onChange={(e) => onChange('compliance', { ...payload.compliance, standard: e.target.value })}
            placeholder={t('cargoDefinitions.fields.compliancePlaceholder')}
            disabled={disabled}
          />
          <TextInput
            label={t('cargoDefinitions.fields.complianceCertRef')}
            name="complianceCertRef"
            value={payload.compliance?.certRef ?? ''}
            onChange={(e) => onChange('compliance', { ...payload.compliance, certRef: e.target.value })}
            placeholder={t('cargoDefinitions.fields.compliancePromptPlaceholder')}
            disabled={disabled}
          />
          <TextInput
            label={t('cargoDefinitions.fields.complianceExpiry')}
            name="complianceExpiry"
            value={payload.compliance?.expiryDate ?? ''}
            onChange={(e) => onChange('compliance', { ...payload.compliance, expiryDate: e.target.value })}
            placeholder={t('cargoDefinitions.fields.compliancePromptPlaceholder')}
            disabled={disabled}
          />
        </div>
      )}
      <p className="pt-2 text-[10px] italic text-slate-500">{PROPERTY_GROUP_HELPER[group]}</p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Tiny shared primitives
// ────────────────────────────────────────────────────────────────────────────

function FieldGroup({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-slate-600">{label}</p>
      {children}
    </div>
  );
}

/** Floating-label content with a trailing red asterisk for required fields. */
function requiredLabel(text: string): React.ReactNode {
  return (
    <>
      {text} <span className="text-red-500">*</span>
    </>
  );
}

function ChipButton({
  active,
  onClick,
  children,
  disabled,
  tone = 'slate',
  title,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  tone?: 'sky' | 'violet' | 'amber' | 'slate' | 'purple';
  title?: string;
}) {
  const palettes = {
    sky: {
      active: 'border-sky-600 bg-sky-600 text-white',
      idle: 'border-slate-300 bg-white text-slate-600 hover:border-sky-400 hover:text-sky-700',
    },
    violet: {
      active: 'border-violet-600 bg-violet-600 text-white',
      idle: 'border-slate-300 bg-white text-slate-600 hover:border-violet-400 hover:text-violet-700',
    },
    amber: {
      active: 'border-amber-600 bg-amber-600 text-white',
      idle: 'border-amber-300 bg-amber-50 text-amber-900 hover:border-amber-500',
    },
    slate: {
      active: 'border-slate-700 bg-slate-700 text-white',
      idle: 'border-slate-300 bg-white text-slate-600 hover:border-slate-500 hover:text-slate-800',
    },
    purple: {
      active: 'border-purple-600 bg-purple-600 text-white',
      idle: 'border-purple-300 bg-purple-50 text-purple-900 hover:border-purple-500',
    },
  } as const;
  const p = palettes[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors disabled:opacity-50 ${
        active ? p.active : p.idle
      }`}
    >
      {children}
    </button>
  );
}

function NumberInput({
  label,
  value,
  onChange,
  allowNegative,
  disabled,
}: {
  label: string;
  value: number | null;
  onChange: (next: number | null) => void;
  allowNegative?: boolean;
  disabled?: boolean;
}) {
  return (
    <TextInput
      label={label}
      name={label}
      type="number"
      value={value == null ? '' : String(value)}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === '') {
          onChange(null);
          return;
        }
        const n = Number(raw);
        if (!Number.isFinite(n)) {
          onChange(null);
          return;
        }
        if (!allowNegative && n < 0) {
          onChange(0);
          return;
        }
        onChange(n);
      }}
      disabled={disabled}
    />
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Order line preview — live "what the operator sees on an order" pane
//
// Sits on the right of the form and updates as the operator ticks chips,
// types names, fills group fields. Three blocks:
//   1. Header on order — cargo type · display name + UoM options.
//   2. Items / variants — for aggregator types lists the compatible
//      child types the operator can add as line items; for everything
//      else lists each ticked variant (preset + custom) with a summary
//      of its inherent properties.
//   3. Inherited from definition — property groups with values, in
//      <"group: summary"> rows.
//   4. Prompted at order entry — property groups ticked but blank, in
//      dashed-amber rows so they stand out as "the operator will be
//      asked".
//   5. Service tags applied — chips for the ticked tags.
//
// All strings here are inline English for now (i18n follow-up). The
// rest of the editor is fully localised.
// ────────────────────────────────────────────────────────────────────────────

interface OrderLinePreviewProps {
  draft: CargoDefinitionDraft;
  variantPresets: CargoVariantPreset[];
  availableServiceTags: ServiceTag[];
}

function OrderLinePreview({ draft, variantPresets, availableServiceTags }: OrderLinePreviewProps) {
  const isAggregator = isAggregatorCargoType(draft.cargoType);
  const allowedLineTypes = ALLOWED_LINE_TYPES[draft.cargoType];

  // Picked preset variants resolve through the catalogue so we get the
  // full inherent properties (LM / PT / dimensions / temp band).
  const pickedPresetVariants = draft.variantPresetIds
    .map((id) => variantPresets.find((v) => v.id === id))
    .filter((v): v is CargoVariantPreset => v != null);

  // Custom variants are only shown once they have a name — incomplete
  // rows (operator just clicked +Add) would clutter the preview.
  const namedCustomVariants = draft.customVariants.filter((c) => c.name.trim().length > 0);

  // Group state partitioned into inherited (filled) vs prompted
  // (ticked but blank). BIC and Compartments markers always land in
  // their respective buckets via `groupHasValues` semantics.
  const activeGroups = PROPERTY_GROUP_KEYS.filter((g) => isGroupActive(g, draft.propertyGroups));
  const inheritedGroups = activeGroups.filter((g) => groupHasValues(g, draft.propertyGroups));
  const promptedGroups = activeGroups.filter((g) => !groupHasValues(g, draft.propertyGroups));

  // Selected service tags resolved against the available catalogue —
  // any ID in the draft that has since been retired from the catalogue
  // is silently dropped.
  const selectedTags = availableServiceTags.filter((t) => draft.serviceTagIds.includes(t.id));

  return (
    <div className="space-y-3 text-[12px]">
      <header className="flex items-center gap-2 pb-1">
        <Package className="h-4 w-4 text-slate-600" />
        <h3 className="text-xs font-semibold text-slate-800">Order line preview</h3>
      </header>
      <p className="text-[11px] italic text-slate-500">
        What the operator sees on an order when they pick this definition.
      </p>

      {/* Header on order */}
      <div className="rounded-md border border-slate-200 bg-slate-50 p-2">
        <p className="text-[10px] uppercase tracking-wide text-slate-500">Header on order</p>
        <p className="font-semibold text-slate-900">
          {getCargoTypeLabel(draft.cargoType)} ·{' '}
          <span className="text-slate-700">{draft.displayName || '— unnamed —'}</span>
        </p>
        <p className="pt-1 text-[11px] text-slate-600">
          UoM options:{' '}
          {draft.unitsOfMeasure.length > 0 ? (
            draft.unitsOfMeasure.map((u) => getRatingUomLabel(u)).join(' · ')
          ) : (
            <span className="italic text-slate-400">none ticked</span>
          )}
        </p>
      </div>

      {/* Items / variants */}
      {isAggregator ? (
        <div className="space-y-1">
          <p className="text-[11px] font-medium text-slate-700">Items on order</p>
          <div className="rounded-md border border-dashed border-blue-300 bg-blue-50/50 p-2 text-[11px] text-blue-900">
            <div className="flex items-center gap-1 font-medium">
              <Plus className="h-3 w-3" />
              Add item — pickable from your definitions of:
            </div>
            <ul className="ml-3 mt-1 list-disc">
              {allowedLineTypes.map((t) => (
                <li key={t}>{getCargoTypeLabel(t)}</li>
              ))}
            </ul>
            <p className="pt-1 text-[10px] italic text-blue-700/70">
              Operator can also enter ad-hoc lines (free text + quantity).
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-1">
          <p className="text-[11px] font-medium text-slate-700">Operator picks a variant</p>
          {pickedPresetVariants.length === 0 && namedCustomVariants.length === 0 ? (
            <p className="text-[11px] italic text-amber-600">
              No variants ticked yet — the operator wouldn&apos;t be able to pick anything at order time.
            </p>
          ) : (
            <div className="rounded-md border border-dashed border-emerald-300 bg-emerald-50/50 p-2">
              <ul className="space-y-0.5 text-[11px] text-emerald-900">
                {pickedPresetVariants.map((v) => (
                  <li key={v.id} className="flex items-baseline gap-1">
                    <ChevronRight className="h-3 w-3 flex-shrink-0 text-emerald-700" />
                    <span className="font-medium">{v.name}</span>
                    <span className="text-emerald-700/70">{variantPresetSummary(v)}</span>
                  </li>
                ))}
                {namedCustomVariants.map((c) => (
                  <li key={c.localId} className="flex items-baseline gap-1">
                    <ChevronRight className="h-3 w-3 flex-shrink-0 text-emerald-700" />
                    <span className="font-medium">{c.name}</span>
                    <span className="text-emerald-700/70">
                      {customVariantSummary(c)} · <em>custom</em>
                    </span>
                  </li>
                ))}
              </ul>
              <p className="pt-1 text-[10px] italic text-emerald-700/70">
                Operator enters product reference + quantity on the picked variant.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Inherited groups */}
      <div className="space-y-1">
        <p className="text-[11px] font-medium text-slate-700">Inherited from definition</p>
        {inheritedGroups.length === 0 ? (
          <p className="text-[11px] italic text-slate-400">
            No constraints set — the order has no preset values from this definition.
          </p>
        ) : (
          <ul className="ml-1 space-y-0.5">
            {inheritedGroups.map((g) => (
              <li
                key={g}
                className="flex items-baseline gap-1 rounded bg-slate-50 px-2 py-0.5 text-[11px] text-slate-700"
              >
                <ChevronRight className="h-3 w-3 flex-shrink-0 text-slate-400" />
                <span className="font-medium">{PROPERTY_GROUP_LABELS[g]}:</span>
                <span className="text-slate-500">
                  {describeFilledGroup(g, draft.propertyGroups)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Prompted groups */}
      <div className="space-y-1">
        <p className="text-[11px] font-medium text-slate-700">Prompted at order entry</p>
        {promptedGroups.length === 0 ? (
          <p className="text-[11px] italic text-slate-400">
            No empty groups — nothing extra to ask the operator.
          </p>
        ) : (
          <ul className="ml-1 space-y-0.5">
            {promptedGroups.map((g) => (
              <li
                key={g}
                className="flex items-baseline gap-1 rounded border border-dashed border-amber-300 bg-amber-50/50 px-2 py-0.5 text-[11px] text-amber-900"
              >
                <ChevronRight className="h-3 w-3 flex-shrink-0 text-amber-600" />
                <span className="font-medium">{PROPERTY_GROUP_LABELS[g]}:</span>
                <span className="text-amber-700">{describePromptedGroup(g)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Service tags */}
      {selectedTags.length > 0 && (
        <div className="space-y-1">
          <p className="text-[11px] font-medium text-slate-700">Service tags applied</p>
          <div className="flex flex-wrap gap-1">
            {selectedTags.map((tag) => (
              <span
                key={tag.id}
                className="inline-flex items-center rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-900"
              >
                {tag.name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Compact summary line for a preset variant — LM / PT / dims / temp band. */
function variantPresetSummary(v: CargoVariantPreset): string {
  const parts: string[] = [];
  if (v.loadingMetres != null) parts.push(`${v.loadingMetres} LM`);
  if (v.perTrailer != null) parts.push(`${v.perTrailer}PT`);
  if (v.dimensions?.length && v.dimensions?.width) {
    parts.push(
      `${v.dimensions.length}×${v.dimensions.width}${v.dimensions.height ? `×${v.dimensions.height}` : ''} cm`,
    );
  }
  if (v.dimensions?.tareWeight) parts.push(`tare ${v.dimensions.tareWeight}kg`);
  if (v.capacity?.maxWeight) parts.push(`max ${v.capacity.maxWeight}kg`);
  if (v.capacity?.maxVolume) parts.push(`${v.capacity.maxVolume}m³`);
  if (v.temperatureBand) parts.push(`${v.temperatureBand.min}…${v.temperatureBand.max}°C`);
  if (v.maxTransitHours) parts.push(`max ${v.maxTransitHours}h transit`);
  return parts.join(' · ') || '—';
}

/** Compact summary line for a custom variant — same shape, draft fields. */
function customVariantSummary(c: CustomVariantDraft): string {
  const parts: string[] = [];
  if (c.loadingMetres != null) parts.push(`${c.loadingMetres} LM`);
  if (c.perTrailer != null) parts.push(`${c.perTrailer}PT`);
  if (c.maxTransitHours != null) parts.push(`max ${c.maxTransitHours}h transit`);
  if (c.minTempC != null || c.maxTempC != null) {
    parts.push(`${c.minTempC ?? '?'}…${c.maxTempC ?? '?'}°C`);
  }
  if (c.lengthCm != null && c.widthCm != null) {
    parts.push(
      `${c.lengthCm}×${c.widthCm}${c.heightCm != null ? `×${c.heightCm}` : ''} cm`,
    );
  }
  if (c.tareWeightKg != null) parts.push(`tare ${c.tareWeightKg}kg`);
  return parts.join(' · ') || '—';
}

// Re-export aggregator helper for callers that want to gate UI on it.
export { AGGREGATOR_CARGO_TYPES };
